import React from 'react';
import styled from 'styled-components';
import { Phone } from 'lucide-react';
import { analytics, trackClick } from '../services/analytics';
import { theme } from '../styles/theme';

interface PlatformWizardProps {
  onSelectPlatform: (platform: 'sleeper' | 'espn' | 'yahoo' | 'direct') => void;
}

const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 1.5rem;
  position: relative;
`;


const WizardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1200px;
  position: relative;
  z-index: 2;
`;

const DisclaimerChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 193, 7, 0.15);
  border: 1px solid rgba(255, 193, 7, 0.3);
  color: #fbbf24;
  font-size: ${theme.typography.sizes.xs};
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 20px;
  margin-bottom: 2rem;
  backdrop-filter: blur(10px);
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  box-shadow: 0 4px 12px rgba(255, 193, 7, 0.1);
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.sizes.xs};
    padding: 6px 12px;
  }
  
  @media (max-width: 480px) {
    font-size: 10px;
    padding: 4px 8px;
    gap: 4px;
  }
`;

const ExpertTitle = styled.h1`
  font-family: 'Poppins', ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes['4xl']};
  font-weight: 500;
  color: ${theme.colors.text.primary};
  text-align: center;
  margin: 0 0 1rem 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.sizes['3xl']};
  }
`;

const ExpertSubtitle = styled.p`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.lg};
  color: ${theme.colors.text.secondary};
  text-align: center;
  margin: 0 0 3rem 0;
  max-width: 600px;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.sizes.base};
    margin-bottom: 2rem;
  }
`;

const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 2rem;
  width: 100%;
  max-width: 800px;
  margin-bottom: 2rem;
  padding: 0 2rem;
  
  @media (max-width: 1024px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto;
    max-width: 350px;
    padding: 0;
    gap: 1.5rem;
  }
`;

const TextButton = styled.div`
  ${theme.glassContainer.normal}
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: 180px;
  padding: 20px;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &:hover {
    ${theme.glassContainer.strong}
    transform: translateY(-8px) scale(1.03);
    box-shadow: ${theme.shadows.glassStrong};
  }
  
  &:active {
    transform: translateY(-4px) scale(1.01);
  }
  
  &::before {
    content: '🏈';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(15deg);
    font-size: 120px;
    opacity: 0.1;
    z-index: 0;
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 350px;
    aspect-ratio: 2 / 1;
    min-height: 120px;
    
    &::before {
      font-size: 80px;
    }
  }
  
  @media (max-width: 480px) {
    min-height: 100px;
    padding: 16px;
    
    &::before {
      font-size: 70px;
    }
  }
`;

const DirectCallContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
  
  span {
    color: ${theme.colors.text.primary};
    font-family: ${theme.typography.fontFamily};
    font-size: ${theme.typography.sizes.xl};
    font-weight: ${theme.typography.weights.semibold};
    line-height: 1.4;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    span {
      font-size: ${theme.typography.sizes.lg};
    }
  }
  
  @media (max-width: 480px) {
    span {
      font-size: ${theme.typography.sizes.base};
    }
  }
`;

const DirectCallSubtext = styled.div`
  color: ${theme.colors.text.secondary};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.normal};
  line-height: 1.3;
  max-width: 280px;
  margin-top: 0.25rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.sizes.xs};
    max-width: 240px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
    max-width: 200px;
  }
`;

const PhoneButton = styled.a`
  ${theme.glassContainer.normal}
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  max-width: 800px;
  padding: 16px 24px;
  margin: 1rem auto;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.1) 100%);
  border: 1px solid rgba(16, 185, 129, 0.3);
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  text-align: center;
  
  &:hover {
    background: linear-gradient(135deg, rgba(16, 185, 129, 0.25) 0%, rgba(5, 150, 105, 0.2) 100%);
    border-color: rgba(16, 185, 129, 0.5);
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.2);
  }
  
  @media (max-width: 768px) {
    margin: 1rem 20px;
    max-width: calc(100% - 40px);
    padding: 14px 20px;
  }
  
  @media (max-width: 480px) {
    margin: 0.75rem 20px;
    padding: 12px 16px;
    gap: 6px;
  }
`;

const PhoneCallText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${theme.typography.sizes.base};
  font-weight: ${theme.typography.weights.semibold};
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.sizes.sm};
  }
`;

const PhoneNumber = styled.div`
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.normal};
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: ${theme.typography.sizes.xs};
  }
`;

const PlatformCard = styled.div<{ $logo: string }>`
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: 180px;
  border-radius: ${theme.borderRadius.lg};
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  position: relative;
  overflow: hidden;
  box-shadow: ${theme.shadows.lg};
  background-image: url(${props => props.$logo});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border: 2px solid ${theme.colors.glass.border};

  &:hover {
    transform: translateY(-8px) scale(1.03);
    box-shadow: ${theme.shadows.glassStrong};
    border-color: ${theme.colors.glass.borderStrong};
  }

  &:active {
    transform: translateY(-4px) scale(1.01);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    max-width: 350px;
    aspect-ratio: 2 / 1;
  }
`;



const PlatformWizard: React.FC<PlatformWizardProps> = ({ onSelectPlatform }) => {
  const platforms = [
    {
      id: 'espn' as const,
      logo: '/img/espn-ff.webp'
    },
    {
      id: 'yahoo' as const,
      logo: '/img/yahoo-ff.jpg'
    },
    {
      id: 'sleeper' as const,
      logo: '/img/sleeper-ff.webp'
    }
  ];

  return (
    <WizardContainer>
      <WizardContent>
        <DisclaimerChip>
          ⚠️ Yobi Labs project - no support offered
        </DisclaimerChip>
        
        <ExpertTitle>
          Talk to your Fantasy Football AI Expert
        </ExpertTitle>
        
        <ExpertSubtitle>
          Have us get your league info, or just talk to the AI expert
        </ExpertSubtitle>
        
        <PlatformGrid>
          <TextButton
            onClick={() => {
              // Track direct talk selection
              analytics.trackPlatformSelection('direct');
              trackClick('platform_direct', 'navigation', { 
                platform: 'direct',
                platform_name: 'direct_talk' 
              });
              onSelectPlatform('direct');
            }}
          >
            <DirectCallContent>
              <span>Start the Call</span>
              <DirectCallSubtext>OR you can customize it further by getting custom league data</DirectCallSubtext>
            </DirectCallContent>
          </TextButton>
          
          {platforms.map(platform => (
            <PlatformCard 
              key={platform.id}
              $logo={platform.logo}
              onClick={() => {
                // Track platform selection
                analytics.trackPlatformSelection(platform.id);
                trackClick(`platform_${platform.id}`, 'navigation', { 
                  platform: platform.id,
                  platform_name: platform.id 
                });
                onSelectPlatform(platform.id);
              }}
            />
          ))}
        </PlatformGrid>
        
        <PhoneButton 
          href="tel:+14154831881"
          onClick={() => trackClick('phone_call_option', 'external_link', {
            phone_number: '415-483-1881',
            button_context: 'platform_selection'
          })}
        >
          <PhoneCallText>
            <Phone size={20} />
            Call
          </PhoneCallText>
          <PhoneNumber>(415) 483-1881</PhoneNumber>
        </PhoneButton>
      </WizardContent>
    </WizardContainer>
  );
};

export default PlatformWizard;
